/**
 * useMyKpis — unified my-KPIs store backed by `/api/v2/kpis`.
 *
 * Single source of truth for the Mijn-omgeving KPI card, the home Layer-3
 * snapshot and Overzicht. Role-based seeds and self-made KPIs are the same
 * `custom_kpi` asset (differ only by `origin`); both are deletable and
 * orderable. Ordering is persisted server-side (`profile.kpi_order`); the home
 * pulse strip renders the first 4.
 *
 * Module-level singleton (like `pulse`): a redundant load is cheap but the
 * shared state means one delete/reorder reflects across every surface.
 */
import {
  createMyKpi,
  deleteMyKpi,
  listMyKpis,
  reorderMyKpis,
  updateMyKpi,
} from '@/features/datasets/api/KpiAssetsRepository';
import { afterCriticalContent } from '@/utils/deferNonCritical';
import { computed, ref } from 'vue';

const kpis = ref([]);
const isLoading = ref(false);
const loaded = ref(false);

export async function loadMyKpis() {
  isLoading.value = true;
  try {
    kpis.value = await listMyKpis();
    loaded.value = true;
  } catch {
    kpis.value = [];
  } finally {
    isLoading.value = false;
  }
  return kpis.value;
}

/**
 * Home snapshot variant: show the skeleton now, fetch once the page's main
 * content (pulse strip) has settled. Returns the cancel function.
 */
export function loadMyKpisAfterCriticalContent() {
  if (!loaded.value) isLoading.value = true;
  return afterCriticalContent(loadMyKpis);
}

export async function createKpi(payload) {
  const created = await createMyKpi(payload);
  await loadMyKpis();
  return created;
}

export async function updateKpi(id, { title, kpiQuery, versionToken } = {}) {
  // The builder produces a fresh query string + title; persist then reload so
  // the recomputed kpi_value (and a new version_token) flow back into the store.
  try {
    const updated = await updateMyKpi(id, { title, kpiQuery, versionToken });
    await loadMyKpis();
    return updated;
  } catch (error) {
    // On failure (e.g. a 409 stale-token conflict) reload so the store holds a
    // fresh version_token and the next edit isn't wedged on the stale one.
    await loadMyKpis();
    throw error;
  }
}

export async function removeKpi(id) {
  // Optimistic removal keeps the card responsive; reload reconciles values.
  const previous = kpis.value;
  kpis.value = previous.filter((k) => k.id !== id);
  try {
    await deleteMyKpi(id);
  } catch (error) {
    kpis.value = previous;
    throw error;
  }
}

export async function reorderKpis(orderedIds) {
  // Optimistic reorder: reflect the new order immediately, persist in the
  // background. On failure, reload restores the server truth.
  const byId = new Map(kpis.value.map((k) => [k.id, k]));
  const next = orderedIds.map((id) => byId.get(id)).filter(Boolean);
  // Append any ids not present in the supplied order (defensive).
  for (const k of kpis.value) {
    if (!orderedIds.includes(k.id)) next.push(k);
  }
  kpis.value = next;
  try {
    await reorderMyKpis(next.map((k) => k.id));
  } catch (error) {
    await loadMyKpis();
    throw error;
  }
}

export function useMyKpis() {
  return {
    kpis: computed(() => kpis.value),
    isLoading: computed(() => isLoading.value),
    loaded: computed(() => loaded.value),
    loadMyKpis,
    createKpi,
    updateKpi,
    removeKpi,
    reorderKpis,
  };
}

export default { loadMyKpis, useMyKpis, createKpi, updateKpi, removeKpi, reorderKpis };
